import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/context/ModalContext";
import { useDirty } from "@/context/DirtyContext";
import {
  createAsset,
  getAssets,
  deleteAsset,
  updateAsset,
  getAsset,
} from "@/api/program-steps";
import { capitalizeFirstWord, safePostMessageToOpener } from "@/lib/utils";
import { useAppSelector } from "@/hooks/redux"
import { addTokensToUrl } from "@/utils/auth"

/**
 * Interface for organization asset type
 */
export interface OrganizationalAssetType {
  id: string;
  name: string;
}

/**
 * Interface for organization asset
 */
export interface OrganizationalAsset {
  id: string;
  name: string;
  items?: number;
  description?: string;
}

/**
 * Interface for the asset request body
 */
export interface CreateAssetRequestBody {
  name: string,
  type: string,
  product_flag: number,
  end_product: string[],
  asset_phase: string[],
  role: string,
  other_roles_requiring_asset?: string[],
  who_requires_asset_role: "All" | "Some",
  quantity_requirement_flag: number,
  min_quantity: number,
  max_quantity: number,
  asset_size_requirement_flag: number,
  unit_of_measurement: string,
  enforce_min_max_asset_size: number,
  min_size?: number,
  max_size?: number,
  size_incremental_distribution_type?: "Fixed" | "Relative",
  allow_asset_reg_with_min_size?: number,
  allowable_percent_below_min_size?: number,
  max_abs_value_below_min_size_flag?: number,
  max_abs_value_below_min_size?: number,
  geographic_address_flag?: number,
  geographic_address_req_type?: "All" | "Some",
  coordinate_point_flag?: number,
  coordinate_point_req_type?: "All" | "Some",
  perimeter_mapping_flag?: number,
  perimeter_mapping_req_type?: "All" | "Some",
  // Organization Asset fields
  category?: "Program Asset" | "Organization Asset",
  organizational_asset_type?: OrganizationalAssetType,
  organizational_asset?: OrganizationalAsset,
  // Badge fields
  geographic_address_badge?: string[],
  point_coordinates_badge?: string[],
  perimeter_mapping_badge?: string[],
  asset_role_badge?: string[],
  user_id: string,
  username: string
}

/**
 * POST /asset
 * Create an asset for a program
 */
export const useCreateAsset = (programId: string, previousUrl: string) => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();
  const { organizationId } = useAppSelector(
    (state) => state.auth.user.managedOrganization ?? {}
  );
  const encodedReturnUrl = encodeURIComponent(previousUrl);


  return useMutation({
    mutationFn: async (payload: CreateAssetRequestBody) => {
        const response = await createAsset(payload, programId);
        return response;
    },
    onMutate: () => {
      showModal("loading")
    },
    onSuccess: ({data}) => {
        queryClient.invalidateQueries({
          queryKey: ["assets"]
        });
        queryClient.invalidateQueries({
          queryKey: ["assets", programId]
        });

        // Reset dirty state to allow easy navigation
        setIsDirty({isDirt: false, confirmAction: () => {}});

        // Notify parent window to refresh its queries
        safePostMessageToOpener({
          type: "ASSET_CREATED",
          programId: programId
        });

        // Only show form redirect if form_id exists
        if (data.form_id) {
          const baseFormBuilderUrl = `${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${data.form_id}?returnUrl=${encodedReturnUrl}`;
          const formBuilderUrl = addTokensToUrl(baseFormBuilderUrl, organizationId);
          showModal("done", {
            title: "Asset has been created successfully",
            description: "You need to create an Asset form to complete the setup",
            onConfirm: () => {
              window.open(formBuilderUrl, '_self');
            },
            onBack: () => window.close(),
            confirmText: "Continue to  Asset Form",
            backText: "Back to Asset"
          });
        } else {
          showModal("done", {
            title: "Asset has been created successfully",
            description: "Your asset has been saved.",
            onConfirm: () => window.close(),
            confirmText: "Done"
          });
        }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description:
          capitalizeFirstWord(error?.error) ||
          "Data unreachable at this time. Please try again later.",
      });
    },
  })
}

/**
 * GET /asset/program/{program_id}
 * Get assets for a specific program
 */
export function useGetAssets(programId: string) {
  return useQuery({
    queryKey: ["assets", programId],
    queryFn: () => getAssets(programId),
    enabled: !!programId,
  });
}

/**
 * DELETE /asset/{asset_id}
 * Delete an asset
 */
export const useDeleteAsset = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();
  return useMutation({
    // We need programId for the onSuccess callback, but not for the actual API call
    mutationFn: (params: { assetId: string; programId: string }) =>
      deleteAsset(params.assetId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      queryClient.invalidateQueries({ queryKey: ["assets", variables.programId] });
      showModal("done", {
        title: "Deleted",
        description: "The asset was deleted successfully.",
        onConfirm: hideModal,
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error?.error || "There was an error deleting the asset.",
      });
    },
  });
};

/**
 * PUT /asset/{asset_id}
 * Update an asset
 */
export const useUpdateAsset = (programId: string, assetId: string, previousUrl: string) => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();
    // Get organization ID from Redux
  const { organizationId } = useAppSelector(
    (state) => state.auth.user.managedOrganization ?? {}
  );
  const encodedReturnUrl = encodeURIComponent(previousUrl);
  return useMutation({
    mutationFn: async (payload: CreateAssetRequestBody) => {
        const response = await updateAsset(payload, programId, assetId);
        return response;
    },
    onMutate: () => {
      showModal("loading")
    },
    onSuccess: ({data}) => {
        queryClient.invalidateQueries({
          queryKey: ["assets"]
        });
        queryClient.invalidateQueries({
          queryKey: ["assets", programId]
        });

        // Reset dirty state to allow easy navigation
        setIsDirty({isDirt: false, confirmAction: () => {}});

        // Notify parent window to refresh its queries
        safePostMessageToOpener({
          type: "ASSET_UPDATED",
          programId: programId
        });

        // Only show form redirect if form_id exists
        if (data.form_id) {
          const baseFormBuilderUrl = `${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${data.form_id}?returnUrl=${encodedReturnUrl}`;
          const formBuilderUrl = addTokensToUrl(baseFormBuilderUrl, organizationId);
          showModal("done", {
            title: "Asset has been updated successfully",
            description: "You need to create an Asset form to complete the setup",
            onConfirm: () => {
              window.open(formBuilderUrl, '_self');
            },
            onBack: () => window.close(),
            confirmText: "Continue to  Asset Form",
            backText: "Back to Asset"
          });
        } else {
          showModal("done", {
            title: "Asset has been updated successfully",
            description: "Your asset has been saved.",
            onConfirm: () => window.close(),
            confirmText: "Done"
          });
        }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description:
          capitalizeFirstWord(error?.error) ||
          "Data unreachable at this time. Please try again later.",
      });
    },
  })
}

/**
 * GET /asset/{asset_id}
 * Get a specific asset
 */
export const useGetAsset = (assetId: string) => {
  return useQuery({
    queryKey: ["assets", "detail", assetId],
    queryFn: () => getAsset(assetId),
    enabled: !!assetId
  });
};
